import React from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Typography,
} from '@mui/material';
import { Trans } from '@lingui/react/macro';
import { useGetMergedHistoryPresentationQuery } from '../../../apis/MergedHistory.api';
import { HistoryType } from '../../../types/models/MergedHistory';
import HistoryListSearch from './HistoryListSearch';
import HistoryListItemRow from './HistoryListItemRow';
import { StyledTableCell } from '../../../../share/components/styled/Table';
import HistoryListPagination from './HistoryListPagination';
import { usePagination } from '../../../../share';

const HistoryListItem = () => {
  const [searchInputAdvanced, setSearchInputAdvanced] = React.useState<any>({});
  const [historyTypes, setHistoryTypes] = React.useState<HistoryType[]>([]);
  const { page, pageSize, handleChangePage, handleChangePageSize } =
    usePagination('MergedHistory');

  const { data, isLoading, isFetching, refetch } =
    useGetMergedHistoryPresentationQuery(
      {
        page,
        pageSize,
        action: searchInputAdvanced.action || undefined,
        updatedAtFrom: searchInputAdvanced.updatedAtFrom || undefined,
        updatedAtTo: searchInputAdvanced.updatedAtTo || undefined,
        cuttingId: searchInputAdvanced.cuttingId || undefined,
        storageId: searchInputAdvanced.storageId || undefined,
        historyTypes: historyTypes.length > 0 ? historyTypes : undefined,
      },
      {
        skip: false,
      },
    );

  const items = data?.items || [];
  const totalItems = data?.totalItems || 0;

  const handleFilterChange = React.useCallback((search: any) => {
    setSearchInputAdvanced(search);
  }, []);

  const handleHistoryTypesChange = React.useCallback((types: HistoryType[]) => {
    setHistoryTypes(types);
  }, []);

  return (
    <Box>
      <Paper data-testid="History-entity-list">
        <HistoryListSearch
          onFilterChange={handleFilterChange}
          onHistoryTypesChange={handleHistoryTypesChange}
          historyTypes={historyTypes}
          refetch={refetch}
        />
        <TableContainer data-testid="History-table-container">
          {isLoading ? (
            <Box
              sx={{ display: 'flex', justifyContent: 'center', p: 3 }}
              data-testid="History-loading"
            >
              <CircularProgress />
            </Box>
          ) : items.length === 0 ? (
            <Box
              sx={{ display: 'flex', justifyContent: 'center', p: 3 }}
              data-testid="History-empty-state"
            >
              <Typography sx={{ color: "text.secondary" }}>
                <Trans>No history records found</Trans>
              </Typography>
            </Box>
          ) : (
            <Table stickyHeader size="small" data-testid="History-table">
              <TableHead>
                <TableRow>
                  <StyledTableCell sx={{ width: 50 }} />
                  <StyledTableCell align="left">
                    <Trans>Date/Time</Trans>
                  </StyledTableCell>
                  <StyledTableCell align="left">
                    <Trans>Type</Trans>
                  </StyledTableCell>
                  <StyledTableCell align="left">
                    <Trans>Action</Trans>
                  </StyledTableCell>
                  <StyledTableCell align="left">
                    <Trans>Key</Trans>
                  </StyledTableCell>
                  <StyledTableCell align="left">
                    <Trans>Volume/Type</Trans>
                  </StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <HistoryListItemRow key={item.id} row={item} />
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
        <HistoryListPagination
          totalItems={totalItems}
          page={page}
          pageSize={pageSize}
          handleChangePage={handleChangePage}
          handleChangePageSize={handleChangePageSize}
        />
        {isFetching && items.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
            <CircularProgress size={20} />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default HistoryListItem;
